import React, { type PropsWithChildren, useEffect } from "react";
import ReactDOM from "react-dom";
import { commitMutation, fetchQuery, graphql, QueryRenderer } from "react-relay";

import {
	faBoxArchive,
	faChevronLeft,
	faChevronRight,
	faClock,
	faCommentAltLines,
	faCopy,
	faLink,
	faSearch,
	faSmile,
	faSquare,
	faTimes,
	faTrashCan,
	faUser,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { OrderedMap } from "immutable";
import { debounce, memoize, noop, sortBy, uniq, uniqBy } from "lodash";
import moment, { type Moment } from "moment";
import { Node as ProseNode } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { Loader } from "semantic-ui-react";

import { Views } from "~/actionItems/constants";
import { CopyToModalRenderer } from "~/actionItems/CopyToModal";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { BlockID } from "~/actionItems/types";
import { assigneeBasedDoneAndWontDo } from "~/actionItems/utils";
import { showFeatureModal, UpgradeIcon } from "~/billing";
import type { BillingFeatureAccessType } from "~/billing/utils";
import { DueDate } from "~/common";
import type { AttendeeAttrs } from "~/common/Attendees";
import { Picker as EmojiPicker } from "~/common/Emoji";
import ViewportContext from "~/common/ViewportContext";
import { getActionItemIntegrationMenus, type IntegrationProvider } from "~/integrations/actionItemIntegration";
import { useForwardedRef } from "~/lib/hooks";
import useBlur from "~/lib/hooks/useBlur";
import useDebouncedValue from "~/lib/hooks/useDebouncedValue";
import useUsers from "~/lib/hooks/useUsers";
import { CustomEmoji } from "~/lib/prose/nodes";
import Comments, { CommentsPluginKey } from "~/lib/prose/plugins/comments";
import { getContext } from "~/lib/prose/plugins/context";
import type { EmojiSearchResult } from "~/lib/prose/plugins/emoji";
import Reactions, { ReactionsPluginKey } from "~/lib/prose/plugins/reactions";
import recaptcha from "~/lib/recaptcha";
import { commitMutationPromise, LRURetainer, RunQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	copyToClipboard,
	getFirstUserFacingError,
	inCalendarMode,
	inVideocallMode,
	isMobile,
	isNative,
	isZoom,
	plural,
	setTooltip,
	type TooltipProps,
} from "~/lib/utils";
import { environment } from "~/models";
import { eventEmitter } from "~/stream/Comments/events";
import { DisabledOnStreamWithExternalUsersTooltip } from "~/stream/DisabledCrossWorkspaceStreamTooltip";
import { Button } from "~/ui";
import { SingleName } from "~/ui/avatar/NameList";
import { DateSelectorDayPicker, type DateSelectorDayPickerProps } from "~/ui/DateSelector";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Avatar from "~/ui/legacy/Avatar";
import { LazyAvatar } from "~/ui/legacy/Avatar";
import Checkbox from "~/ui/legacy/Checkbox";
import Popup from "~/ui/legacy/Popup";
import { HelpCenterLink, Switch, TextField, ToggleGroup } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import { streamStore } from "../stream/utils";
import { linkedItemTooltip } from "./LinkedItemText";
import { jumpToOriginalBlock } from "./useActionItemJump";

import type { AssignableDropdown_GetBlockShortURLMutation as GetBlockShortURLMutation } from "~/__generated__/AssignableDropdown_GetBlockShortURLMutation.graphql";
import type { AssignableDropdownAssigneesByIdQuery } from "~/__generated__/AssignableDropdownAssigneesByIdQuery.graphql";
import AssigneeSelectConcreteQuery, {
	type AssignableDropdownAssigneeSelectQuery,
} from "~/__generated__/AssignableDropdownAssigneeSelectQuery.graphql";
import type { AssignableDropdownInviteAndAssignMutation as InviteAndAssignMutationType } from "~/__generated__/AssignableDropdownInviteAndAssignMutation.graphql";
import type {
	AssignableDropdownQuickDateSelectFirstFutureEventQuery,
	AssignableDropdownQuickDateSelectFirstFutureEventQuery$data,
} from "~/__generated__/AssignableDropdownQuickDateSelectFirstFutureEventQuery.graphql";
import type { AssignableDropdownToggleBlocksFieldMutation } from "~/__generated__/AssignableDropdownToggleBlocksFieldMutation.graphql";

import styles from "./css/assignableDropdown.module.css";

const inviteAndAssignMutation = graphql`
	mutation AssignableDropdownInviteAndAssignMutation($input: InviteTodoAssigneeMutationInput!) {
		inviteTodoAssignee(input: $input) {
			invitee {
				errors {
					field
					message
				}
				user {
					id
					email
					fullName
					isActive
					isConfigured
				}
			}
			event {
				id
				attendees {
					invitable
					email
					fullName
					image
					user {
						id
					}
				}
			}
		}
	}
`;

function NoAssigneeIcon({ className }: { className?: string }) {
	return (
		<div className={cx(styles.noAssigneeIcon, className)}>
			<FontAwesomeIcon icon={faUser} />
		</div>
	);
}

export const AssigneeItem = ({
	user,
	streamType,
	isTodoStream,
	disableExternalDashes,
	isSelected,
}: {
	user: AttendeeAttrs;
	streamType: string;
	isTodoStream?: boolean;
	disableExternalDashes?: boolean;
	isSelected: boolean;
}) => {
	return (
		<div
			className={cx(styles.assigneeItem, {
				[styles.invitable]: user.invitable,
				[styles.externalInNote]: user.isExternalInNote,
				[styles.selectedAssignee]: isSelected,
			})}
		>
			<div className={styles.assigneeItemIcon}>
				<Avatar
					link={false}
					user={user}
					className={styles.avatar}
					dashedOutline={user.isExternalInNote && !disableExternalDashes}
				/>
			</div>
			{!!user.isExternalInNote && ["meeting", "personal"].includes(streamType) && !isTodoStream ? (
				<Popup
					hoverable
					inverted
					mouseLeaveDelay={0}
					size="tiny"
					on={user.isExternalInNote ? ["hover"] : []}
					position="bottom center"
					closeOnPortalMouseLeave
					closeOnTriggerClick
					content={
						<>
							<Popup.Title>Doesn&rsquo;t have access to notes</Popup.Title>
							<Popup.Content>Share the series to give them access to the notes</Popup.Content>
						</>
					}
					trigger={<p className={styles.assigneeItemTitle}>{user.fullName}</p>}
				/>
			) : (
				<p className={styles.assigneeItemTitle}>{user.fullName}</p>
			)}
			{isSelected ? <FontAwesomeIcon icon={faTimes} /> : <div className={styles.assigneeItemActions}>Assign</div>}
		</div>
	);
};

const assigneeSelectQuery = graphql`
	query AssignableDropdownAssigneeSelectQuery($noteId: ID!) {
		node(id: $noteId) {
			... on Note {
				noteuserSet {
					id
					user {
						id
						type
						firstName
						fullName
						email
						image
						isGuest
						isExternal
					}
				}
			}
		}
	}
`;

export const DueDateSelect: React.FunctionComponent<{
	goBack: () => void;
	removeDate: () => void;
	date?: Moment;
	updateDate: (d: Moment, displayName?: string) => void;
	hideHeader?: boolean;
	streamId?: string;
	useQuickDateSelect?: boolean;
}> = ({ goBack, updateDate, date, removeDate, hideHeader, streamId, useQuickDateSelect }) => {
	const handleDaySelectorDateChange = React.useCallback<NonNullable<DateSelectorDayPickerProps["onSelect"]>>(
		(_, selectedDay) => {
			updateDate(moment(selectedDay), undefined);
		},
		[updateDate],
	);

	return (
		<>
			{(!hideHeader || date) && (
				<div className="duedate-topbar">
					<div className="duedate-back-button">
						<div className="icon" onClick={goBack}>
							<FontAwesomeIcon icon={faChevronLeft} />
						</div>
					</div>
					<div className="duedate-due">Due date</div>
				</div>
			)}
			{useQuickDateSelect && <QuickDateSelect updateDate={updateDate} streamId={streamId} />}
			<DateSelectorDayPicker
				selected={date ? date.toDate() : moment().toDate()}
				onSelect={handleDaySelectorDateChange}
				className={styles.dayPicker}
				classNames={{
					// eslint-disable-next-line camelcase
					month_caption: styles.dayPickerCaption,
				}}
			/>
			{date && (
				<Button size="wide" className={styles.removeDate} variant="tertiary" onClick={removeDate}>
					Remove due date
				</Button>
			)}
		</>
	);
};

const firstFutureEvent = graphql`
	query AssignableDropdownQuickDateSelectFirstFutureEventQuery($streamId: ID!) {
		noteStream(id: $streamId) {
			firstFutureEvent {
				start
			}
		}
	}
`;

type QuickDateOption = { displayName: string; date: Moment };

const QuickDateSelect = ({
	updateDate,
	streamId,
}: {
	updateDate: (d: Moment, displayName?: string) => void;
	streamId?: string;
}) => {
	const selectDate = React.useCallback(
		(date, displayName) => {
			updateDate(date, displayName);
		},
		[updateDate],
	);

	const cacheKey = React.useMemo(() => `${streamId}-nextMeeting`, [streamId]);
	const dateFormat = "YYYY-MM-DDTHH:mm:ss+HH:mm";
	let nextMeetingFromCache = streamStore.get(cacheKey);

	const renderQuickDate = React.useCallback(
		({ props }: { props: AssignableDropdownQuickDateSelectFirstFutureEventQuery$data | null }) => {
			const firstFutureEventDate = props?.noteStream?.firstFutureEvent?.start;
			if (!firstFutureEventDate) return null;

			// Cache entry expires on the day of the next meeting
			const dateFormatted = moment(firstFutureEventDate, dateFormat);
			streamStore.setExpire(cacheKey, dateFormatted, dateFormatted);

			return (
				<QuickDateSelectOption displayName={"Before next meeting"} date={dateFormatted} onSelectDate={selectDate} />
			);
		},
		[cacheKey, selectDate],
	);

	return streamId ? (
		nextMeetingFromCache ? (
			<QuickDateSelectOption
				displayName={"Before next meeting"}
				date={moment(nextMeetingFromCache, dateFormat)}
				onSelectDate={selectDate}
			/>
		) : (
			<QueryRenderer<AssignableDropdownQuickDateSelectFirstFutureEventQuery>
				environment={environment}
				query={firstFutureEvent}
				variables={{ streamId: streamId }}
				render={renderQuickDate}
			/>
		)
	) : (
		<></>
	);
};

function QuickDateSelectOption({
	displayName,
	date,
	onSelectDate,
}: QuickDateOption & { onSelectDate: (date: Moment, displayName: string) => void }) {
	const selectDate = React.useCallback(() => {
		onSelectDate(date, displayName);
	}, [date, displayName, onSelectDate]);

	return (
		<Button size="wide" onClick={selectDate} variant="tertiary" className={styles.quickDate}>
			<div>{displayName}</div>
			<div className={styles.dateDisplay}>{date.format("ddd, MMM D")}</div>
		</Button>
	);
}

type AssignableDropdownItemProps = {
	action?: (close: () => void) => boolean;
	toolTip?: TooltipProps;
	className?: string;
	active?: boolean;
	setActive: () => void;
	left?: React.ReactNode;
	icon?: IconDefinition;
	right?: React.ReactNode;
	readOnly?: boolean;
	disabled?: boolean;
	streamHasExternalUsers?: boolean;
	onActive?: () => void;
	close: () => void;
	containerClassName?: string;
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
const AssignableDropdownItem = React.forwardRef<HTMLDivElement, AssignableDropdownItemProps>(
	function AssignableDropdownItem(
		{
			children,
			action,
			onClick: _onClick,
			toolTip,
			className = "",
			active = false,
			setActive,
			left,
			right,
			icon,
			readOnly = false,
			disabled = false,
			close,
			...divProps
		},
		ref: React.Ref<HTMLDivElement>,
	) {
		const onMouseEnter = React.useCallback(() => {
			if (readOnly || disabled) return;
			if (!active) {
				setActive();
			}
		}, [setActive, active, readOnly, disabled]);
		const onFocus = React.useCallback(() => {
			if (!active && !readOnly && !disabled) {
				setActive();
			}
		}, [setActive, active, readOnly, disabled]);
		const onClick = React.useCallback(
			(e: React.MouseEvent) => {
				if (!readOnly && !disabled && action && action(close)) {
					e.stopPropagation();
					e.preventDefault();
				}
			},
			[action, readOnly, disabled, close],
		);
		const inner = (() => {
			const innerLeft = icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : left || null;
			const innerRight = right || null;
			if (innerLeft || innerRight)
				return (
					<div className={styles.itemContent}>
						<div className={styles.itemContentLeft}>{innerLeft}</div>
						<div className={styles.itemContentRight}>{innerRight}</div>
					</div>
				);
			return children;
		})();
		return (
			<div
				className={cx(styles.dropdownItem, className, {
					[styles.dropdownItemActive]: active,
					[styles.dropdownItemReadOnly]: readOnly,
					[styles.dropdownItemDisabled]: disabled,
				})}
				onClick={onClick}
				{...toolTip}
				ref={ref}
				onMouseEnter={onMouseEnter}
				onFocus={onFocus}
				{...divProps}
			>
				{inner}
			</div>
		);
	},
);

export type AssignableDropdownItemType = Partial<PropsWithChildren<AssignableDropdownItemProps>> & {
	id: string;
	renderSubView?: (close: () => void, back: () => void) => React.ReactNode;
};
export type AssignableDropdownMenuSections = ({
	sectionProps?: { className?: string };
	children: AssignableDropdownItemType[];
} | null)[];

export interface DisableOnLinkedCopyArgs {
	originalBlockIds: (string | null)[];
	hasAccessToOriginalNote: boolean;
	blockIdsToNoteIds: { [key: string]: string | undefined };
	isTeams?: boolean;
}

export function disableOnLinkedCopy({
	originalBlockIds,
	hasAccessToOriginalNote,
	blockIdsToNoteIds,
	isTeams = false,
}: DisableOnLinkedCopyArgs) {
	const uniqOriginalBlockIds = uniq(originalBlockIds).filter(Boolean) as string[];
	const singleOriginalBlockId = uniqOriginalBlockIds.length === 1 ? uniqOriginalBlockIds[0] : undefined;
	const originalNoteId = singleOriginalBlockId ? blockIdsToNoteIds[singleOriginalBlockId] : undefined;
	return singleOriginalBlockId && originalNoteId
		? {
				...linkedItemTooltip(hasAccessToOriginalNote),
				renderSubView: undefined,
				action() {
					jumpToOriginalBlock(singleOriginalBlockId, originalNoteId, isTeams);
				},
			}
		: {};
}

export const getDueDateItem = ({
	hasAccessToDuedates,
	setBlocksAttrs,
	dueDates,
	close,
	hasAccessToOriginalNote = true,
	isTeams = false,
	streamId,
	...rest
}: DisableOnLinkedCopyArgs & {
	hasAccessToDuedates: boolean;
	setBlocksAttrs: (values: ProseNode["attrs"]) => unknown;
	dueDates: (string | null)[];
	close: () => void;
	hasAccessToOriginalNote?: boolean;
	streamId?: string;
}): AssignableDropdownItemType => {
	const dueDates2 = uniq(dueDates);
	const uniqueDueDate = dueDates2.length === 1 ? dueDates2[0] : undefined;
	const hasDueDate = dueDates2.filter(Boolean).length;

	return {
		id: "dueDate",
		className: styles.dropdownDueDateItem,
		disabled: !hasAccessToOriginalNote,
		action: () => {
			if (!hasAccessToDuedates) {
				showFeatureModal("duedates");
				close();
				return true;
			}
			return true;
		},
		renderSubView: hasAccessToDuedates
			? (close, back) => {
					const updateDate = (date: Moment | null, displayName?: string) => {
						if (!hasAccessToOriginalNote) return;
						if (!hasDueDate) {
							dispatchAnalyticsTrack("Action Item Due Date Selected", {
								presetOption: displayName,
							});
						}
						setBlocksAttrs({ dueDate: date?.format("YYYY-MM-DD") || null });
						close();
					};
					const removeDate = () => {
						updateDate(null);
						close();
					};
					return (
						<DueDateSelect
							goBack={back}
							date={uniqueDueDate ? moment(uniqueDueDate, "YYYY-MM-DD") : undefined}
							// eslint-disable-next-line react/jsx-no-bind
							removeDate={removeDate}
							// eslint-disable-next-line react/jsx-no-bind
							updateDate={updateDate}
							streamId={streamId}
							useQuickDateSelect
						/>
					);
				}
			: close => {
					setTimeout(() => showFeatureModal("duedates"), 0);
					close();
					return false;
				},
		left: <FontAwesomeIcon icon={faClock} fixedWidth />,
		right: (
			<div className={styles.menuItemWithCrown}>
				{hasDueDate ? (
					uniqueDueDate ? (
						<DueDate long date={uniqueDueDate} />
					) : (
						<span>Multiple due dates set</span>
					)
				) : (
					<span>Set a due date</span>
				)}
				{hasAccessToDuedates ? (
					<FontAwesomeIcon icon={faChevronRight} className={styles.dueDateItemIcon} />
				) : (
					<FontAwesomeIcon icon={faArrowAltCircleUp} color="var(--billingColor)" />
				)}
			</div>
		),
		...disableOnLinkedCopy({ ...rest, hasAccessToOriginalNote, isTeams }),
	};
};

type UpdateItemAttrs = Parameters<typeof getDueDateItem>[0]["setBlocksAttrs"];

export function getCurrentAssigneeMenuItem(
	{
		currentAssignees,
		user,
		currentAssigneeIds, // TODO: look into this --- can we get around having both parameters?
		completedBy,
		statusCondition,
		done,
	}: {
		currentAssignees?: AssigneeType[] | null;
		user?: AssigneeType;
		currentAssigneeIds?: string[] | null;
		completedBy: UserAttrs | null;
		statusCondition: StatusCondition;
		done: boolean;
	},
	{ icon, readOnly = false }: { icon?: IconDefinition; readOnly?: boolean } = {},
) {
	let usernameContainerRef;
	if (!currentAssignees?.length && currentAssigneeIds?.length) {
		let memo: undefined | HTMLDivElement;
		usernameContainerRef = (() => {
			let resolver;
			const p = new Promise<HTMLDivElement>(resolve => {
				resolver = resolve;
			});
			p.then(v => {
				memo = v;
			});
			return {
				promise: p,
				resolver,
				memo,
			};
		})();
	}
	const userAssignee = currentAssignees?.find(assignee => assignee.id === user?.id);

	const firstAssignee = userAssignee ? userAssignee : currentAssignees?.length ? currentAssignees[0] : null;
	const otherAssignees = currentAssigneeIds?.length ? currentAssigneeIds.length - 1 : 0;
	const andOtherText = otherAssignees > 0 && (
		<>
			{" "}
			and {otherAssignees} other{plural(otherAssignees)}
		</>
	);
	return {
		id: "current-assignee",
		className: styles.currentAssignee,
		left: firstAssignee ? (
			<Avatar
				user={firstAssignee}
				link={false}
				className={styles.currentAssigneeAvatar}
				dashedOutline={firstAssignee.isExternalInNote}
			/>
		) : currentAssigneeIds?.length && currentAssigneeIds[0] ? (
			<>
				<LazyAvatar
					link={false}
					usernameContainerPromise={usernameContainerRef?.promise}
					className={styles.currentAssigneeAvatar}
					dashedOutline
					userId={currentAssigneeIds[0]}
					preloader={<></>}
				/>
			</>
		) : (
			<NoAssigneeIcon className={styles.currentAssigneeAvatar} />
		),
		right: (
			<div className={styles.currentAssigneeContent}>
				<div>
					{firstAssignee && currentAssignees?.length ? (
						user && firstAssignee.id == user.id ? (
							<div className={styles.currentAssigneeName}>
								Assigned to <SingleName user={firstAssignee} selfId={user?.id} capitalize={false} />
								{andOtherText}
							</div>
						) : (
							<>
								<div className={styles.currentAssigneeAssignedTo}>Assigned to</div>
								<div className={styles.currentAssigneeName}>
									<SingleName user={firstAssignee} selfId={user?.id} />
								</div>
								{andOtherText}
							</>
						)
					) : currentAssigneeIds?.length ? (
						<>
							<div className={styles.currentAssigneeAssignedTo}>Assigned to</div>
							{usernameContainerRef?.memo || <div ref={usernameContainerRef?.resolver}></div>}
							{andOtherText}
						</>
					) : (
						<div className={styles.currentAssigneeName}>No assignee</div>
					)}
					{done &&
					completedBy &&
					(statusCondition === StatusCondition.SINGLE || !currentAssignees || currentAssignees.length <= 1) ? (
						<span className={styles.completedBy}>Completed by {completedBy.fullName}</span>
					) : (
						<></>
					)}
				</div>
				{icon && <FontAwesomeIcon icon={icon} className={styles.currentAssigneeIcon} />}
			</div>
		),
		readOnly: readOnly,
	};
}

// AssigneeType represents an assignee backed by a Fellow user
export type AssigneeType = {
	id: string; // User id
	fullName: string;
	image?: string;
	isExternalInNote?: boolean;
	email?: string;
};

// AttendeeAssigneeType represents an assignee backed by an attendee from the calendar event.
// It may have a user field if the attendee is a Fellow user that that we recently invited.
type AttendeeAssigneeType = {
	email: string;
	fullName: string;
	image: string;
	invitable: boolean;
	isExternal: boolean;
	isGuest: boolean;
	user: {
		id: string;
	} | null;
};

function getUserIdFromAssignee(assignee: AssigneeType | AttendeeAssigneeType): string {
	return "id" in assignee ? assignee.id : (assignee.user?.id ?? "");
}

export function getReassignMenu(
	props: {
		item?: ProseItemType;
		noteId?: string;
		blockKey?: string;
		creatorId?: string | null;
		eventGuid?: string | undefined;
		streamName: string;
		displayedAssigneeOptions: AttendeeAttrs[];
		allAssigneeOptions: AttendeeAttrs[];
		streamType: string;
		isTodoStream: boolean;
		updateItemAttrs: UpdateItemAttrs;
		currentAssigneeMenuItem?: AssignableDropdownItemType;
		currentAssignees?: (AssigneeType | AttendeeAssigneeType)[];
		setSelectedAssignees?: React.Dispatch<React.SetStateAction<(AssigneeType | AttendeeAssigneeType)[] | undefined>>;
		reRenderEditor?: () => void;
		afterInvite?: () => void;
		loading?: boolean;
		searchLoading?: boolean;
		searchContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
		onSearchItemActive?: () => void;
		isSearching?: boolean;
		nodeType: string;
		getAllAssigneeData?: boolean;
		disableExternalDashes?: boolean;
		allowMultipleAssigneeSelection?: boolean;
		toggleMultipleAssignees?: (checked: boolean) => void;
		statusCondition?: StatusCondition;
		changeMultiAssigneeStatusCondition?: (value: string) => void;
		assignToAll?: (e: React.ChangeEvent<HTMLInputElement>) => void;
		areAllNoteUsersAssigned?: boolean;
		unassignAll?: () => boolean;
		itemHasActiveIntegration?: boolean;
		hasAccessToMultipleAssignees?: boolean;
		allowCompleteByAny?: boolean;
	} & ({ noModal?: false } | { noModal: true }),
	options: {
		showAssigneesHeading?: boolean;
	} = {},
): (back?: () => void) => (close: () => void) => AssignableDropdownMenuSections {
	const {
		item,
		displayedAssigneeOptions: displayedUsers,
		allAssigneeOptions,
		updateItemAttrs,
		currentAssignees,
		currentAssigneeMenuItem,
		setSelectedAssignees: setCurrentAssigneesInSelect,
		streamType,
		isTodoStream,
		noteId,
		eventGuid,
		blockKey,
		reRenderEditor,
		afterInvite,
		loading = false,
		searchLoading = false,
		searchContainerRef,
		onSearchItemActive,
		isSearching,
		nodeType,
		disableExternalDashes,
		allowMultipleAssigneeSelection,
		toggleMultipleAssignees,
		statusCondition,
		changeMultiAssigneeStatusCondition,
		getAllAssigneeData,
		assignToAll,
		areAllNoteUsersAssigned,
		unassignAll,
		itemHasActiveIntegration,
		hasAccessToMultipleAssignees,
		allowCompleteByAny,
	} = props;

	const currentAssigneeIds = currentAssignees?.map(getUserIdFromAssignee) || [];
	const currentAssigneeIdsSet = new Set(currentAssigneeIds);

	return (back?: () => void) => {
		const sortedAssigneeOptions = [...allAssigneeOptions].sort((a, b) => {
			const userIdA = a.id || a.user?.id || a.email;
			const userIdB = b.id || b.user?.id || b.email;
			if (currentAssigneeIdsSet.has(userIdA) && !currentAssigneeIdsSet.has(userIdB)) {
				return -1;
			}
			if (!currentAssigneeIdsSet.has(userIdA) && currentAssigneeIdsSet.has(userIdB)) {
				return 1;
			}
			const fullNameComparison = a.fullName.localeCompare(b.fullName);
			if (fullNameComparison !== 0) {
				return fullNameComparison;
			}
			// Very rare, only if names are the exact same
			if (a.invitable && !b.invitable) {
				return -1;
			}
			return 1;
		});
		let sortedUsers = !isSearching ? sortedAssigneeOptions : displayedUsers;
		if (nodeType === "agenda") {
			sortedUsers = sortedUsers.filter(u => !u.isExternalInNote);
		}
		return memoize((close: () => void) => {
			function maybePaywalledToggleMultipleAssignees(checked: boolean) {
				if (!hasAccessToMultipleAssignees) {
					showFeatureModal("multiple_assignees");
					return;
				}
				toggleMultipleAssignees?.(checked);
			}
			return [
				{
					children: [
						...(toggleMultipleAssignees
							? [
									{
										id: "multiple-assignee-header",
										children: (
											<>
												<div className="duedate-back-button">
													<div className="icon" onClick={back}>
														<FontAwesomeIcon icon={faChevronLeft} />
													</div>
												</div>
												<div>Allow multiple assignees</div>
												<div className={styles.menuItemWithCrown}>
													<Switch
														size="small"
														checked={allowMultipleAssigneeSelection}
														// eslint-disable-next-line fellow/no-inline-disable
														// eslint-disable-next-line react/jsx-no-bind
														onCheckedChange={maybePaywalledToggleMultipleAssignees}
														disabled={!allowMultipleAssigneeSelection && Boolean(itemHasActiveIntegration)}
														disabledTooltip={"Action items connected to an integration can only have one assignee"}
														tooltipPlacement={"top"}
													/>
												</div>
											</>
										),
										className: styles.multipleAssigneeConfig,
										readOnly: true,
									},
								]
							: []),
						...(allowMultipleAssigneeSelection && statusCondition && changeMultiAssigneeStatusCondition
							? [
									{
										id: "assignee-any-all-toggle",
										children: (
											<>
												Complete by
												<ToggleGroup
													className={styles.anyAllToggle}
													options={[{ value: "All" }, { value: "Any" }]}
													value={statusCondition === "SINGLE" ? "Any" : "All"}
													onValueChange={changeMultiAssigneeStatusCondition}
													tooltips={{
														Any: "Only one of the assignees must complete the action item",
														All: "Each assignee must individually complete the action item",
													}}
													disabled={statusCondition === "ALL" && !allowCompleteByAny}
													disabledTooltip="Complete by any was disabled by the workspace administrator"
												/>
											</>
										),
										className: styles.multipleAssigneeConfig,
										readOnly: true,
									},
								]
							: []),
					],
				},
				{
					children: [
						...(currentAssigneeMenuItem
							? [
									{
										...currentAssigneeMenuItem,
										action(): boolean {
											updateItemAttrs({ assignees: [] });
											close();
											return false;
										},
									},
								]
							: []),
					],
				},
				{
					sectionProps: {
						className: styles.assigneeListMenuSection,
					},
					children: [
						...(options.showAssigneesHeading && !loading && !searchContainerRef // If there a search box, don't show no-assignee message
							? [
									{
										id: "assignee-list-header",
										right: displayedUsers.filter(u => !currentAssigneeIdsSet.has(u.id)).length ? (
											<>These teammates have access:</>
										) : (
											<>
												No other teammates have access{" "}
												{isTodoStream
													? "since this item was created in your action items section."
													: streamType === "personal"
														? "since this item is from a Private Series."
														: ""}
											</>
										),
										readOnly: true,
										className: styles.addedByItem,
									},
								]
							: []),
						...(loading
							? [
									{
										id: "loader",
										children: <Loader active inline />,
										disabled: true,
										style: { display: "flex", justifyContent: "center" },
									},
								]
							: []),
						...(searchContainerRef && !isMobile && !inVideocallMode && !inCalendarMode
							? [
									{
										id: "search",
										left: loading ? <Loader active size="small" /> : <FontAwesomeIcon icon={faSearch} />,
										right: <div ref={searchContainerRef} className={styles.assigneeSearchContainer}></div>,
										readOnly: true,
										className: styles.assigneeSearchInputContainer,
										onActive: onSearchItemActive,
									},
								]
							: []),
						...(allowMultipleAssigneeSelection && !isTodoStream && !isSearching
							? [
									{
										id: "select-all",
										left: (
											<Checkbox
												checked={areAllNoteUsersAssigned}
												onChange={assignToAll}
												green
												className={styles.allNoteUsersCheckbox}
											/>
										),
										right: "All note users",
										style: { paddingBottom: "0.6rem" },
										readOnly: true,
									},
								]
							: []),
						...sortedUsers
							.filter(attendee => {
								if (!attendee.user && !attendee.invitable) return false;
								const userId = attendee.id || attendee.user?.id || attendee.email;
								return !(nodeType === "agenda" && (!userId || currentAssigneeIdsSet.has(userId)));
							})
							.map(attendee => {
								const userId = attendee.id || attendee.user?.id;
								return {
									id: `assignee-${userId || attendee.email}`,
									className: styles.assigneeItemContainer,
									action: () => {
										const promise = Promise.resolve(true);
										promise.then(async ok => {
											if (!ok) return;
											if (userId) {
												let newAssignees: string[] = [];
												let removingSingleAssignee = false;

												if (currentAssigneeIds) {
													const assigneeIdx = currentAssigneeIds.indexOf(userId);
													if (assigneeIdx > -1) {
														// Already assigned to this user
														if (allowMultipleAssigneeSelection) {
															currentAssigneeIds.splice(assigneeIdx, 1);
															newAssignees = currentAssigneeIds;
														} else {
															removingSingleAssignee = true;
															newAssignees = [];
														}
													} else if (allowMultipleAssigneeSelection) {
														newAssignees = [userId, ...currentAssigneeIds];
													} else {
														newAssignees = [userId];
													}
												} else {
													newAssignees = [userId];
												}
												// Update the state to show which assignees have been selected
												const selectedUsers = allAssigneeOptions.filter(a =>
													newAssignees.includes(getUserIdFromAssignee(a)),
												);
												if (setCurrentAssigneesInSelect) setCurrentAssigneesInSelect(selectedUsers);

												if (removingSingleAssignee) {
													updateItemAttrs({ assignees: [] });
													return;
												}
												const assigneesMap = new Map<string, AssigneeStatusType>();
												item?.assignees.forEach(assignee => {
													assigneesMap.set(assignee.id, assignee);
												});
												let assigneesMarkedAsDone = 0;
												let assigneesMarkedAsWontDo = 0;
												const newAssigneesWithStatuses = newAssignees.map(a => {
													const status = assigneesMap.has(a)
														? assigneesMap.get(a)?.status || NoteBlockStatus.NOT_DONE
														: NoteBlockStatus.NOT_DONE;
													assigneesMarkedAsDone += status === NoteBlockStatus.DONE ? 1 : 0;
													assigneesMarkedAsWontDo += status === NoteBlockStatus.WONT_DO ? 1 : 0;
													return {
														id: a,
														status,
													};
												});
												let attrs: {
													assignees: Array<AttendeeAttrs> | AssigneeStatusType[];
													done?: boolean;
													wontDo?: boolean;
													statusCondition?: StatusCondition;
												} = {
													assignees: getAllAssigneeData ? selectedUsers : newAssigneesWithStatuses,
													statusCondition,
												};
												if (attrs.assignees.length <= 1 && attrs.statusCondition === StatusCondition.ALL) {
													attrs.statusCondition = StatusCondition.SINGLE;
													const { done, wontDo } = assigneeBasedDoneAndWontDo(
														newAssigneesWithStatuses,
														attrs.statusCondition,
														item?.done ?? false,
														item?.wontDo ?? false,
													);
													attrs = {
														...attrs,
														done,
														wontDo,
													};
												}
												if (statusCondition === StatusCondition.ALL && attrs.assignees.length > 1) {
													const newDone = assigneesMarkedAsDone === attrs.assignees.length;
													const newWontDo = assigneesMarkedAsWontDo === attrs.assignees.length;
													if (newDone !== item?.done || newWontDo !== item?.wontDo) {
														attrs = {
															...attrs,
															done: newDone,
															wontDo: newWontDo,
														};
													}
												}
												updateItemAttrs(attrs);

												if (!allowMultipleAssigneeSelection) close();
												return;
											}

											if (!attendee.invitable || !noteId || !blockKey) return;

											const captchaToken = await recaptcha("assignable_invite");

											commitMutation<InviteAndAssignMutationType>(environment, {
												mutation: inviteAndAssignMutation,
												variables: {
													input: {
														noteId,
														eventGuid,
														captchaToken,
														noteBlockKey: blockKey,
														invitation: {
															email: attendee.email,
															fullName: attendee.fullName,
															sendInviteEmail: true,
														},
													},
												},
												onCompleted({ inviteTodoAssignee }, err) {
													const beforeReturn = () => {
														afterInvite?.();
														reRenderEditor?.();
														close();
													};
													if (err && err.length) {
														const errorMessage =
															getFirstUserFacingError(err) ?? `Something went wrong sending the invitation: ${err}`;
														beforeReturn();
														return toast.error(errorMessage);
													}

													if (!inviteTodoAssignee?.invitee) return toast.error("Invitation failed");

													const { errors, user } = inviteTodoAssignee.invitee;
													if (errors && errors.length) {
														const message = errors
															.map(error => error && error.message)
															.filter(Boolean)
															.join(", ");
														beforeReturn();
														return toast.error(`Invitation failed: ${message}`);
													}
													if (!user) return toast.error("Invitation failed");
													// TODO: ensure this works with multiple assignees
													const newAssignees = [getUserIdFromAssignee(user), ...(currentAssigneeIds ?? [])].map(
														assigneeId => ({
															id: assigneeId,
														}),
													);
													updateItemAttrs({ assignees: newAssignees, done: false });
													beforeReturn();
													toast.success(`Invite sent to ${user.fullName}`);
													return;
												},
											});
										});
										return true;
									},
									children: (
										<AssigneeItem
											user={attendee}
											streamType={streamType}
											isTodoStream={isTodoStream}
											disableExternalDashes={disableExternalDashes}
											isSelected={!!currentAssignees?.find(a => userId === getUserIdFromAssignee(a))}
										/>
									),
								};
							}),
						...(displayedUsers.length === 0 && !loading && !searchLoading
							? [
									{
										id: "empty_participants",
										children: <div className={styles.assigneeNoUsers}>No users found</div>,
										disabled: true,
										style: { paddingLeft: 0, paddingRight: 0 },
									},
								]
							: []),
					],
				},
				...(unassignAll && currentAssigneeIds.length >= 1
					? [
							{
								children: [
									{
										id: "unassign-all",
										children: <>Unassign all</>,
										style: { color: "var(--veryVividRed)" },
										action: unassignAll,
									},
								],
							},
						]
					: []),
			];
		});
	};
}

export const trapKeyboardEvents = (e: React.KeyboardEvent<unknown>) => {
	e.stopPropagation();
	e.nativeEvent.stopPropagation();
	e.nativeEvent.stopImmediatePropagation();
};

export function getCommentMenuItem({ view, blockKey }: { view: EditorView; blockKey: string }) {
	const ext = view.extensions.get("block-comments") as Comments;
	const currentBlockCommentInfo = CommentsPluginKey.getState(view.state)?.commentsData[blockKey];
	const currentBlockCount = currentBlockCommentInfo?.count;
	const hasAccessToComments = getContext(view.state)?.billing?.hasAccessToComments;
	return {
		id: "addComments",
		icon: faCommentAltLines,
		right: (
			<div className={styles.menuItemWithCrown}>
				<span>Add a comment…</span>
				{!hasAccessToComments && <UpgradeIcon />}
				{hasAccessToComments && !!currentBlockCount && (
					<span className={styles.commentsContentLabel}>{currentBlockCount}</span>
				)}
			</div>
		),
		action: (close: () => void) => {
			if (!hasAccessToComments) {
				showFeatureModal("comments");
				close();
				return true;
			}
			if (!ext) {
				close();
				return true;
			}

			close();
			eventEmitter.emit("NoteComments.open", ext.noteId, blockKey);
			return true;
		},
	};
}

export function getDeleteBlockMenu({
	view,
	getPos,
	text,
	callback,
}: {
	view: EditorView;
	getPos: () => number;
	text: string;
	callback?: () => void;
}) {
	return {
		id: "deleteBlock",
		icon: faTrashCan,
		right: text,
		action: (close: () => void) => {
			if (!view.editable) return false;
			const start = getPos();
			const node = view.state.doc.resolve(start).nodeAfter;
			if (!node) return false;
			close();
			view.dispatch(view.state.tr.deleteRange(start, start + node.nodeSize));

			if (callback) callback();
			return true;
		},
		disabled: !view.editable,
	};
}

const assigneeSelectRetainer = new LRURetainer(10);

export const AssigneeSelect: React.FC<{
	close: () => void;
	goBack: () => void;
	updateItemAttrs: UpdateItemAttrs;
	currentAssigneesFromParticipants?: (AssigneeType | AttendeeAssigneeType)[];
	participants?: AttendeeAttrs[];
	item: ProseItemType;
	note?: { id: string };
	afterInvite?: () => void;
	stream: { type: string; displayName: string; isTodoStream: boolean; isExternal: boolean };
	showHeading?: boolean;
	inNote?: boolean;
	noModal?: boolean;
	getAllAssigneeData?: boolean;
	disableExternalDashes?: boolean;
	itemHasActiveIntegration?: boolean;
	hasAccessToMultipleAssignees: boolean;
	allowCompleteByAny: boolean;
}> = ({
	goBack,
	updateItemAttrs,
	participants,
	currentAssigneesFromParticipants,
	showHeading,
	item,
	note,
	afterInvite,
	stream,
	close,
	inNote = false,
	noModal = false,
	getAllAssigneeData = false,
	disableExternalDashes = false,
	itemHasActiveIntegration,
	hasAccessToMultipleAssignees,
	allowCompleteByAny,
}) => {
	assigneeSelectRetainer.retain(AssigneeSelectConcreteQuery, { noteId: note?.id });
	const { isMobile } = React.useContext(ViewportContext);
	const [queryLoading, setQueryLoading] = React.useState(!participants && !!note?.id);
	const [noteUsersProps, setNoteUsersProps] = React.useState<AssignableDropdownAssigneeSelectQuery["response"] | null>(
		null,
	);

	const [allowMultipleAssigneeSelection, setAllowMultipleAssigneeSelection] = React.useState(
		!!(item.assignees && item.assignees.length > 1),
	);
	const [statusCondition, setStatusCondition] = React.useState<StatusCondition>(item.statusCondition);

	const [selectedAssignees, setSelectedAssignees] = React.useState<(AssigneeType | AttendeeAssigneeType)[] | undefined>(
		currentAssigneesFromParticipants,
	);

	const [temporarilyDeselectedAssignees, setTemporarilyDeselectedAssignees] = React.useState<
		(AssigneeType | AttendeeAssigneeType)[]
	>([]);

	useEffect(() => {
		// Determine which assignees that are on the prose node do not exist in the note participants (prose context)
		// The prose context will include these assignees when the context refreshed (note is reloaded)
		const assigneeIdsExternalToNote: string[] | undefined = item.assignees
			?.filter((a: AssigneeStatusType) => !participants?.find((p: AttendeeAttrs) => p.id === a.id))
			.map((a: AssigneeStatusType) => a.id);

		if (assigneeIdsExternalToNote.length === 0) return;

		// Get the user records for these external assignees
		fetchQuery<AssignableDropdownAssigneesByIdQuery>(environment, assigneesByIdQuery, {
			userIds: assigneeIdsExternalToNote,
		})
			.toPromise()
			.then(value => {
				if (!value?.usersById) return;
				const assignees = [...(currentAssigneesFromParticipants ?? []), ...value.usersById];
				const assigneesUniqueById = uniqBy(assignees, getUserIdFromAssignee);
				setSelectedAssignees(assigneesUniqueById);
			});
	}, [currentAssigneesFromParticipants, item.assignees, participants]);

	const [searchText, setSearchText] = React.useState("");
	const onSearchItemActive = React.useCallback(() => {
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, []);

	const variables = React.useMemo(() => ({ noteId: note?.id || "" }), [note?.id]);
	const searchInputRef = React.useRef<HTMLInputElement>(null);
	// Due to a scenario with nested portals here we need to force the focus to the search
	// input on click
	const onSearchClick = React.useCallback(() => searchInputRef.current?.focus(), [searchInputRef]);
	const debouncedSearchText = useDebouncedValue(searchText, 300);
	const searchContainerRef = React.useRef<HTMLDivElement>(null);
	const onQueryResult = React.useCallback(
		(result: AssignableDropdownAssigneeSelectQuery["response"]) => {
			setQueryLoading(false);
			setNoteUsersProps(result);
		},
		[setQueryLoading, setNoteUsersProps],
	);
	const { data: usersData, loading: usersLoading } = useUsers(debouncedSearchText, {
		perPage: 10,
		// Do not ignore empty searches if a note was not sent
		ignoreEmptySearch: !!note,
	});
	const onQueryLoading = React.useCallback(() => {
		setQueryLoading(true);
	}, [setQueryLoading]);

	const noteUsers = React.useMemo(() => {
		return (
			noteUsersProps?.node?.noteuserSet?.map(nu => ({
				...nu.user,
				user: nu.user,
				__typename: "User",
				invitable: false,
			})) || []
		);
	}, [noteUsersProps?.node?.noteuserSet]);

	const selectedAssigneeIds = React.useMemo(() => selectedAssignees?.map(getUserIdFromAssignee), [selectedAssignees]);
	const areAllNoteUsersAssigned = React.useMemo(
		() => noteUsers.every(u => selectedAssigneeIds?.includes(u.id)),
		[noteUsers, selectedAssigneeIds],
	);

	const usersFromNote = React.useMemo(() => {
		return uniqBy([...noteUsers, ...(participants || [])], item => (item.invitable ? item.email : item.id));
	}, [noteUsers, participants]);

	const nonQueryAssigneeOptions: AttendeeAttrs[] = React.useMemo(() => {
		if (!noteUsersProps && note?.id) {
			return participants || [];
		}

		const participantIds = participants?.map(p => p.id);
		const newAssigneesExternalToNote = selectedAssignees?.filter(
			a => !participantIds?.includes(getUserIdFromAssignee(a)),
		);
		const newAssigneesAsAttendees: AttendeeAttrs[] | undefined = newAssigneesExternalToNote?.map(a => {
			return {
				__typename: "User",
				id: getUserIdFromAssignee(a),
				email: a.email ?? "",
				type: "MEMBER",
				invitable: false,
				fullName: a.fullName,
				image: a.image ?? "",
				user: undefined,
				isGuest: false,
				isExternal: false,
				isExternalInNote: "isExternalInNote" in a ? a.isExternalInNote : false,
			};
		});
		const userAttendees = participants?.filter(p => p.user && !p.id) || [];
		const nonUserAttendees = participants?.filter(p => !p.user) || [];
		return [
			...uniqBy([...usersFromNote, ...(newAssigneesAsAttendees ?? [])], user => user?.id).filter(a => a.user),
			...userAttendees,
			...nonUserAttendees,
		];
	}, [note, usersFromNote, noteUsersProps, participants, selectedAssignees]);

	const queryAssigneeOptions: AttendeeAttrs[] = React.useMemo(() => {
		if (!noteUsersProps && note?.id) {
			return participants || [];
		}

		const userIdsFromNote = new Set(usersFromNote.map(u => u.id));
		const queryUsers =
			usersData?.viewer.users?.edges
				.map(e => e.node)
				.map(u => ({
					...u,
					user: u,
					isExternalInNote: !userIdsFromNote.has(u.id),
					__typename: "User",
					invitable: !!u.internalAttributes?.invitable,
				})) || [];
		return uniqBy(queryUsers, user => user?.id);
	}, [note?.id, usersFromNote, noteUsersProps, participants, usersData?.viewer.users?.edges]);

	const allAssigneeOptions = React.useMemo(() => {
		return uniqBy([...nonQueryAssigneeOptions, ...queryAssigneeOptions], user => user?.id || user.email);
	}, [nonQueryAssigneeOptions, queryAssigneeOptions]);

	const displayedAssigneeOptions: AttendeeAttrs[] | [] = React.useMemo(() => {
		// nonQueryAssigneeOptions are filtered locally in memory from notes users and participants.
		// We do not want to filter queryAssigneeOptions because it is already filtered by the backend.
		const nonQuerySearchResults = nonQueryAssigneeOptions.filter(
			a => a.fullName.toLowerCase().includes(searchText.toLowerCase()) || a.email.includes(searchText.toLowerCase()),
		);
		const searchResults = uniqBy([...nonQuerySearchResults, ...queryAssigneeOptions], user => user?.id || user.email);

		return sortBy(searchResults, [i => i.fullName.toLowerCase()]);
	}, [nonQueryAssigneeOptions, queryAssigneeOptions, searchText]);

	const isSearching = !!searchText;
	const loading = !participants && !noteUsersProps && queryLoading;
	const searchLoading = isSearching && (usersLoading || searchText !== debouncedSearchText);
	const changeMultiAssigneeStatusCondition = React.useCallback(
		(value: string) => {
			const statusCondition =
				value.toUpperCase() === "ANY" || value === "SINGLE" ? StatusCondition.SINGLE : StatusCondition.ALL;
			let attrs: { [key: string]: unknown } = { statusCondition: statusCondition };
			if (item?.assignees) {
				const { done, wontDo } = assigneeBasedDoneAndWontDo(item.assignees, statusCondition, item.done, item.wontDo);
				attrs = {
					...attrs,
					done,
					wontDo,
				};
			}
			updateItemAttrs(attrs);
			setStatusCondition(statusCondition);
		},
		[updateItemAttrs, item],
	);

	const unassignAll = React.useCallback(() => {
		const empty: [] = [];
		setSelectedAssignees(empty);
		updateItemAttrs({ assignees: empty, statusCondition: StatusCondition.SINGLE });
		return true;
	}, [updateItemAttrs]);

	const storeDeselectedAssignees = React.useCallback(() => {
		setTemporarilyDeselectedAssignees(selectedAssignees ?? []);
	}, [selectedAssignees]);

	const loadDeselectedAssignees = React.useCallback(() => {
		if (temporarilyDeselectedAssignees.length > 0) setSelectedAssignees(temporarilyDeselectedAssignees);
	}, [temporarilyDeselectedAssignees]);

	const toggleMultipleAssignees = React.useCallback(
		(checked: boolean) => {
			if (!allowMultipleAssigneeSelection && checked) {
				if (itemHasActiveIntegration) return;
				// Changing from single to multiple assignees, want to default to ALL status condition
				// If complete by all is enabled
				// This is also done in the result of the mutation, but it looks better to toggle the pill state instantly
				setStatusCondition(StatusCondition.ALL);
				if (temporarilyDeselectedAssignees.length === 0) changeMultiAssigneeStatusCondition(StatusCondition.ALL);
				loadDeselectedAssignees();
			} else if (allowMultipleAssigneeSelection && !checked) {
				// Changing from multiple to single assignee, want to show everyone as unassigned (but not actually propagate it)
				// and have ANY status condition
				changeMultiAssigneeStatusCondition(StatusCondition.SINGLE);
				storeDeselectedAssignees();
				setSelectedAssignees([]);
			}
			setAllowMultipleAssigneeSelection(checked);
		},
		[
			allowMultipleAssigneeSelection,
			itemHasActiveIntegration,
			temporarilyDeselectedAssignees.length,
			changeMultiAssigneeStatusCondition,
			loadDeselectedAssignees,
			storeDeselectedAssignees,
		],
	);

	const applyAssignmentToAllNoteUsers = React.useCallback(() => {
		const assigning = !areAllNoteUsersAssigned;

		const noteUserIds = noteUsers.map(u => u.id);
		const newAssignees = assigning
			? uniqBy([...(selectedAssignees ?? []), ...noteUsers], getUserIdFromAssignee)
			: (selectedAssignees?.filter(a => !noteUserIds.includes(getUserIdFromAssignee(a))) ?? []);
		setSelectedAssignees(newAssignees);

		const newAssigneesWithStatuses = newAssignees.map(a => {
			return {
				id: getUserIdFromAssignee(a),
				status: item?.assignees?.find(i => i.id === getUserIdFromAssignee(a))?.status ?? NoteBlockStatus.NOT_DONE,
			};
		});

		updateItemAttrs({
			assignees: getAllAssigneeData ? newAssignees : newAssigneesWithStatuses,
		});
	}, [areAllNoteUsersAssigned, getAllAssigneeData, item?.assignees, noteUsers, selectedAssignees, updateItemAttrs]);

	const reassignMenu = getReassignMenu(
		{
			item: item,
			noModal: inNote || noModal,
			streamName: stream.displayName,
			streamType: stream.type,
			isTodoStream: stream.isTodoStream,
			updateItemAttrs,
			currentAssignees: selectedAssignees,
			setSelectedAssignees: setSelectedAssignees,
			displayedAssigneeOptions: displayedAssigneeOptions,
			allAssigneeOptions: allAssigneeOptions,
			loading,
			searchLoading,
			searchContainerRef,
			noteId: note?.id,
			afterInvite,
			blockKey: item?.key,
			isSearching: isSearching,
			onSearchItemActive,
			nodeType: item?.type || "",
			getAllAssigneeData,
			disableExternalDashes,
			allowMultipleAssigneeSelection,
			toggleMultipleAssignees,
			statusCondition,
			changeMultiAssigneeStatusCondition,
			assignToAll: applyAssignmentToAllNoteUsers,
			areAllNoteUsersAssigned,
			unassignAll,
			itemHasActiveIntegration,
			hasAccessToMultipleAssignees,
			allowCompleteByAny,
		},
		{ showAssigneesHeading: showHeading },
	);
	return (
		<>
			{searchContainerRef.current &&
				!isMobile &&
				!stream.isExternal &&
				ReactDOM.createPortal(
					<div onKeyDown={trapKeyboardEvents} onClick={onSearchClick}>
						<TextField
							value={searchText}
							setValue={setSearchText}
							withCross
							ref={searchInputRef}
							placeholder="Assign to..."
							className={styles.searchInput}
						/>
					</div>,
					searchContainerRef.current,
				)}
			{note && (
				<RunQuery<AssignableDropdownAssigneeSelectQuery>
					query={assigneeSelectQuery}
					variables={variables}
					fetchPolicy={"store-or-network"}
					onResult={onQueryResult}
					onLoading={onQueryLoading}
				/>
			)}
			<AssignableDropdownMenu close={close} menuSections={reassignMenu(goBack)} />
		</>
	);
};

export type AssigneeStatusType = {
	id: string;
	status: NoteBlockStatus;
};

export type ProseItemType = {
	id: string;
	done: boolean;
	wontDo: boolean;
	text: string;
	assignees: AssigneeStatusType[] | [];
	statusCondition: StatusCondition;
	key: string;
	creatorId: string | null;
	type: string;
};

export function getLazyReassignMenuItem({
	updateItemAttrs,
	currentAssigneesFromParticipants,
	participants,
	user,
	item,
	note,
	afterInvite,
	stream,
	showHeading = true,
	hasAccessToOriginalNote = false,
	disabled = false,
	inNote = false,
	noModal = false,
	originalBlockId = null,
	isTeams = false,
	getAllAssigneeData = false,
	disableExternalDashes = false,
	itemHasActiveIntegration,
	hasAccessToMultipleAssignees,
	allowCompleteByAny,
	completedBy,
	done,
}: {
	updateItemAttrs: UpdateItemAttrs;
	currentAssigneesFromParticipants?: AssigneeType[];
	participants?: AttendeeAttrs[];
	user?: { id: string; fullName: string; image?: string; email?: string };
	item: ProseItemType;
	note?: { id: string };
	afterInvite?: () => void;
	stream: { type: string; displayName: string; isTodoStream: boolean; isExternal: boolean };
	showHeading?: boolean;
	hasAccessToOriginalNote?: boolean;
	disabled?: boolean;
	inNote?: boolean;
	noModal?: boolean;
	originalBlockId: string | null;
	isTeams: boolean;
	getAllAssigneeData: boolean;
	disableExternalDashes: boolean;
	itemHasActiveIntegration?: boolean;
	hasAccessToMultipleAssignees: boolean;
	allowCompleteByAny: boolean;
	completedBy: UserAttrs | null;
	done: boolean;
}) {
	const currentAssigneeMenuItem = getCurrentAssigneeMenuItem(
		{
			currentAssignees: currentAssigneesFromParticipants,
			user,
			currentAssigneeIds: item.assignees ? item.assignees.map(a => a.id) : [],
			completedBy,
			done,
			statusCondition: item.statusCondition,
		},
		{ icon: faChevronRight },
	);

	return {
		...currentAssigneeMenuItem,
		id: "reassign",
		disabled: !hasAccessToOriginalNote || disabled,
		renderSubView(close: () => void, back: () => void) {
			return (
				<React.Suspense fallback={<Loader />}>
					<AssigneeSelect
						close={close}
						goBack={back}
						currentAssigneesFromParticipants={currentAssigneesFromParticipants}
						updateItemAttrs={updateItemAttrs}
						participants={participants}
						item={item}
						note={note}
						afterInvite={afterInvite}
						stream={stream}
						showHeading={showHeading}
						noModal={noModal}
						inNote={inNote}
						getAllAssigneeData={getAllAssigneeData}
						disableExternalDashes={disableExternalDashes}
						itemHasActiveIntegration={itemHasActiveIntegration}
						hasAccessToMultipleAssignees={hasAccessToMultipleAssignees}
						allowCompleteByAny={allowCompleteByAny}
					/>
				</React.Suspense>
			);
		},
		...disableOnLinkedCopy({
			hasAccessToOriginalNote,
			originalBlockIds: [originalBlockId],
			blockIdsToNoteIds: note && originalBlockId ? { [originalBlockId]: note.id } : {},
			isTeams,
		}),
	};
}

export function getReassignMenuItem({
	view,
	currentAssigneeMenuItem,
	reassignMenu,
	...rest
}: DisableOnLinkedCopyArgs & {
	view: EditorView;
	reassignMenu: (back: () => void) => (close: () => void) => AssignableDropdownMenuSections;
	currentAssigneeMenuItem: AssignableDropdownItemType;
}) {
	return {
		...currentAssigneeMenuItem,
		id: "reassign",
		renderSubView(close: () => void, back: () => void) {
			return <AssignableDropdownMenu close={close} menuSections={reassignMenu(back)} />;
		},
		disabled: !view.editable,
		...disableOnLinkedCopy(rest),
	};
}

export function getCreatorMenuItem({
	user,
	creator,
	isAiGenerated = false,
}: {
	user?: { id: string; fullName: string };
	creator: { id: string; fullName: string } | null;
	isAiGenerated?: boolean;
}) {
	return {
		id: "creator",
		className: styles.addedByItem,
		children: isAiGenerated ? (
			<span>AI-Detected</span>
		) : creator ? (
			<span>
				Added by <SingleName user={creator} selfId={user?.id} />
			</span>
		) : null,
		readOnly: true,
	};
}

export function getCopyToMenuItem(num = 1) {
	return {
		id: "copy-to",
		icon: faCopy,
		right: <>Copy {num > 1 && <b>({num})</b>} to...</>,
		disabled: false,
	};
}

export function getCopyLinkMenuItem(blockId: { id: string } | { noteId: string; blockKey: string }) {
	return {
		id: "copy-link",
		icon: faLink,
		right: <>Copy link</>,
		action(close: () => void) {
			async function getShortUrl() {
				const result = await commitMutationPromise<GetBlockShortURLMutation>(environment, {
					mutation: graphql`
						mutation AssignableDropdown_GetBlockShortURLMutation($blockId: BlockID!) {
							NoteBlock_GetShortURL(blockId: $blockId) {
								url
							}
						}
					`,
					variables: { blockId },
				});
				if (!result.NoteBlock_GetShortURL) throw new Error();
				return result.NoteBlock_GetShortURL.url;
			}

			copyToClipboard(getShortUrl())
				.then(() => {
					toast.success("Copied link to clipboard");
				})
				.catch(error => {
					Sentry.captureException(error);
					console.error(error);
					toast.error("Failed to copy link, please try again later");
				});
			close();
			return true;
		},
	};
}

const toggleBlockFieldMutation = graphql`
	mutation AssignableDropdownToggleBlocksFieldMutation($ids: [BlockID!], $value: Boolean!) {
		toggleBlocksField(input: { ids: $ids, field: WONT_DO, value: $value }) {
			noteBlockEdges {
				node {
					id
					done
					wontDo
				}
			}
		}
	}
`;

function updateWontDo(
	ids: BlockID[],
	wontDo: boolean,
	statusCondition: StatusCondition,
	assignees: Array<AssigneeStatusType> | null,
	user: AttendeeAttrs | undefined,
	setBlockAttrs: (values: ProseNode["attrs"]) => unknown,
) {
	if (statusCondition === StatusCondition.ALL && assignees && assignees.length > 1) {
		const newAssignees = assignees.map(assignee => {
			return { ...assignee };
		});
		for (let i = 0; i < newAssignees.length; i++) {
			if (newAssignees[i].id !== user?.id) continue;
			newAssignees[i].status =
				newAssignees[i].status === NoteBlockStatus.WONT_DO ? NoteBlockStatus.NOT_DONE : NoteBlockStatus.WONT_DO;
			break;
		}
		setBlockAttrs({ assignees: newAssignees });
		return;
	}
	commitMutation<AssignableDropdownToggleBlocksFieldMutation>(environment, {
		mutation: toggleBlockFieldMutation,
		variables: {
			ids,
			value: wontDo,
		},
	});
}

export function getWontDoMenuItem<T extends BlockID>({
	ids,
	allWontDo,
	statusCondition,
	assignees,
	user,
	setBlockAttrs,
	onClick = updateWontDo,
}: {
	allWontDo: boolean;
	ids: T[];
	statusCondition: StatusCondition;
	assignees: Array<AssigneeStatusType> | null;
	user: AttendeeAttrs | undefined;
	setBlockAttrs: (values: ProseNode["attrs"]) => unknown;
	onClick?: (
		ids: BlockID[],
		wontDo: boolean,
		statusCondition: StatusCondition,
		assignees: Array<AssigneeStatusType> | null,
		user: AttendeeAttrs | undefined,
		setBlockAttrs: (values: ProseNode["attrs"]) => unknown,
	) => void;
}) {
	return {
		id: "toggle-wont-do",
		icon: allWontDo ? faSquare : faBoxArchive,
		right: allWontDo ? (
			<>Mark {ids.length > 1 && <b>({ids.length})</b>} as incomplete</>
		) : (
			<>Archive {ids.length > 1 && <b>({ids.length}) action items</b>}</>
		),
		action(close: () => void) {
			onClick(ids, !allWontDo, statusCondition, assignees, user, setBlockAttrs);
			close();
			return true;
		},
	};
}

export function getReactionMenuItem({ blockKey, editorView }: { blockKey: string; editorView: EditorView }) {
	const reactionsInfo = ReactionsPluginKey.getState(editorView.state)?.reactionsData[blockKey];
	const isFull = Object.keys(reactionsInfo || {}).length >= 10;

	return {
		disabled: isFull,
		id: "add-reaction",
		icon: faSmile,
		right: "Add a reaction…",
		containerClassName: styles.reactionMenuContainer,
		renderSubView(close: () => void, _back: () => void) {
			const ext = editorView.extensions.get("block-reactions");
			if (!(ext instanceof Reactions)) {
				close();
				return false;
			}
			let accountId = "";
			const customEmojiExt = editorView.extensions.get("custom_emoji");
			if (customEmojiExt instanceof CustomEmoji) {
				accountId = customEmojiExt.accountId;
			}
			const { isMobile } = getContext(editorView.state);
			return (
				<EmojiPicker
					autoFocus
					emojiTooltip
					native
					accountId={accountId}
					emoji=""
					perLine={isMobile ? undefined : 7}
					darkMode={document.documentElement.dataset.theme === "theme-dark"}
					style={{ border: "none", ...(isMobile ? { width: "100%" } : {}) }}
					// eslint-disable-next-line react/jsx-no-bind
					onClick={(emoji: EmojiSearchResult, event) => {
						const ident = emoji.colons ?? emoji.id;
						if (!ident) {
							console.warn("Emoji has no .colons nor .id... what is it?", emoji);
							close();
							return;
						}
						ext.addReaction(blockKey, ident);
						if (!event.shiftKey) close();
					}}
					// eslint-disable-next-line react/jsx-no-bind
					onSelect={(emoji: EmojiSearchResult) => {
						const ident = emoji.colons ?? emoji.id;
						if (!ident) {
							console.warn("Emoji has no .colons nor .id... what is it?", emoji);
							close();
							return;
						}
						ext.addReaction(blockKey, ident);
						close();
					}}
					skinEmoji="+1"
					title=""
				/>
			);
		},
	};
}

const disabledIntegrationMultipleAssigneesTooltip = setTooltip("Disabled for action items with multiple assignees", {
	width: "12rem",
});

const assigneesByIdQuery = graphql`
	query AssignableDropdownAssigneesByIdQuery($userIds: [ID]) {
		usersById(userIds: $userIds) {
			id
			email
			fullName
			image
			isGuest
		}
	}
`;

export function getActionItemMenu({
	hasAccessToDuedates,
	hasAccessToJiraIntegration,
	hasAccessToAsana,
	hasAccessToMultipleAssignees,
	connectedActionItemIntegrations,
	itemHasActiveIntegration,
	billingFeatureAccess,
	dueDate,
	creatorId,
	assignees,
	statusCondition,
	setBlockAttrs,
	done,
	wontDo,
	participants,
	streamId,
	streamName,
	streamType,
	streamIsExternal,
	streamHasExternalUsers,
	isTodoStream,
	noteId,
	afterInvite,
	view,
	getPos,
	blockKey,
	user,
	jira,
	hasAccessToOriginalNote,
	originalBlockId,
	isAccountAdmin,
	isTeams,
	allowCompleteByAny,
	editorView,
	completedBy,
	aiGenerated,
}: {
	creatorId: string | null;
	assignees: AssigneeStatusType[] | null;
	statusCondition: StatusCondition;
	dueDate: string | null;
	done: boolean;
	wontDo: boolean;
	streamId: string;
	streamName: string;
	streamType: string;
	streamIsExternal: boolean;
	streamHasExternalUsers: boolean;
	isTodoStream: boolean;
	participants: AttendeeAttrs[];
	hasAccessToDuedates: boolean;
	hasAccessToJiraIntegration: boolean;
	hasAccessToAsana: boolean;
	hasAccessToMultipleAssignees: boolean;
	connectedActionItemIntegrations: IntegrationProvider[];
	itemHasActiveIntegration: boolean;
	billingFeatureAccess?: BillingFeatureAccessType;
	setBlockAttrs: (values: ProseNode["attrs"]) => unknown;
	blockKey: string;
	noteId: string;
	view: EditorView;
	getPos: () => number;
	originalBlockId: string | null;
	user?: AttendeeAttrs;
	jira?: { id: string; clientKey: string; baseUrl: string };
	hasAccessToOriginalNote: boolean;
	eventGuid?: string;
	afterInvite?: () => void;
	reRenderEditor?: () => void;
	isAccountAdmin: boolean | undefined;
	isGuest: boolean | undefined;
	isTeams: boolean;
	allowCompleteByAny: boolean;
	editorView: EditorView;
	completedBy: UserAttrs | null;
	aiGenerated: boolean;
}): (close: () => void) => AssignableDropdownMenuSections {
	const context = getContext(view.state);

	return close => {
		const assigneeIds = assignees?.map(a => a.id);
		const currentAssigneesFromParticipants: AttendeeAttrs[] = [
			...participants.filter(
				p => assigneeIds && (assigneeIds.includes(p.id) || (p.user && assigneeIds.includes(p.user.id))),
			),
		];
		// For external users, we only want to see note users as potential assignees. For internal users,
		// it's OK to see assignees external to the note.
		const noteUserParticipantsOnlyForExternalStreams = participants.filter(
			p => !streamIsExternal || !p.isExternalInNote,
		);
		let actions = [
			{
				children: view.editable
					? [
							getDueDateItem({
								dueDates: [dueDate],
								setBlocksAttrs: setBlockAttrs,
								hasAccessToDuedates,
								close,
								hasAccessToOriginalNote: hasAccessToOriginalNote && view.editable,
								originalBlockIds: [originalBlockId],
								blockIdsToNoteIds: originalBlockId ? { [originalBlockId]: noteId } : {},
								isTeams,
								streamId,
							}),
						]
					: [],
			},
			{
				children: [
					getLazyReassignMenuItem({
						participants: noteUserParticipantsOnlyForExternalStreams,
						updateItemAttrs: setBlockAttrs,
						currentAssigneesFromParticipants: currentAssigneesFromParticipants,
						item: {
							done,
							wontDo,
							text: "",
							id: blockKey,
							assignees: assignees ?? [],
							statusCondition,
							key: blockKey,
							creatorId,
							type: "todo",
						},
						note: { id: noteId },
						afterInvite,
						stream: { type: streamType, displayName: streamName, isTodoStream, isExternal: streamIsExternal },
						user,
						showHeading: false,
						hasAccessToOriginalNote,
						disabled: !hasAccessToOriginalNote || !view.editable,
						inNote: true,
						isTeams,
						originalBlockId,
						getAllAssigneeData: false,
						disableExternalDashes: false,
						itemHasActiveIntegration,
						hasAccessToMultipleAssignees,
						allowCompleteByAny,
						completedBy,
						done,
					}),
				],
			},
		];

		let copySection: AssignableDropdownMenuSections[0] = {
			children:
				!context.isPrivateNote && view.editable
					? [
							{
								...getCopyToMenuItem(),
								action: (close: () => void) => {
									close();
									__globalModalContext?.open(({ close }) => (
										<CopyToModalRenderer ids={[{ noteId, key: blockKey }]} close={close} />
									));
									return true;
								},
								disabled: streamHasExternalUsers,
								streamHasExternalUsers: streamHasExternalUsers,
							},
						]
					: [],
		};

		const showWontDo =
			statusCondition !== StatusCondition.ALL ||
			!assignees ||
			assignees.length <= 1 ||
			assignees.some(assignee => assignee.id === user?.id);

		if (showWontDo && view.editable) {
			let wontDoStatus = wontDo;
			if (statusCondition === StatusCondition.ALL && assignees) {
				wontDoStatus =
					assignees.find(assignee => assignee.id === user?.id)?.status === NoteBlockStatus.WONT_DO || wontDo;
			}
			copySection.children.push(
				getWontDoMenuItem({
					ids: [{ noteId, blockKey }],
					allWontDo: wontDoStatus,
					statusCondition,
					assignees,
					user,
					setBlockAttrs,
				}),
			);
		}

		actions.push(copySection);

		const multipleAssignees = Boolean(assignees && assignees?.length > 1);

		if (!isNative && !user?.isGuest && !streamIsExternal && view.editable) {
			actions.push({
				children: getActionItemIntegrationMenus({
					billingFeatureAccess,
					hasAccessToJiraIntegration,
					connectedActionItemIntegrations,
					setBlockAttrs,
					view,
					getPos,
					close,
					jira,
					isAccountAdmin,
					originalBlockIds: [originalBlockId],
					blockIdsToNoteIds: originalBlockId ? { [originalBlockId]: noteId } : {},
					hasAccessToOriginalNote,
					isTeams,
					blockKey,
					noteId,
					hasAccessToAsana,
					disabled: multipleAssignees,
					tooltip: multipleAssignees ? disabledIntegrationMultipleAssigneesTooltip : undefined,
				}),
			});
		}

		actions.push(
			{
				children: context.isMeetingModal
					? [getReactionMenuItem({ blockKey, editorView })]
					: !context.isPrivateNote
						? [getReactionMenuItem({ blockKey, editorView }), getCommentMenuItem({ blockKey, view: editorView })]
						: [],
			},
			{
				children: context.isMeetingModal
					? [getDeleteBlockMenu({ view, getPos, text: "Delete" })]
					: [
							...(noteId ? [getCopyLinkMenuItem({ noteId: noteId, blockKey })] : []),
							getDeleteBlockMenu({ view, getPos, text: "Delete" }),
						],
			},
			{
				children: !context.isPrivateNote
					? [
							getCreatorMenuItem({
								creator: participants.find(u => u.id === creatorId) ?? null,
								user,
								isAiGenerated: aiGenerated,
							}),
						]
					: [],
			},
		);

		return actions;
	};
}

export const getItemsForProse =
	({
		creatorId,
		assigneeId,
		setBlockAttrs,
		participants,
		noteId,
		eventGuid,
		blockKey,
		getPos,
		reRenderEditor,
		afterInvite,
		user,
		streamName,
		streamType,
		streamHasExternalUsers,
		isTodoStream,
		editorView,
		includeCopyTo,
		nodeType,
		completedBy,
		done,
		statusCondition,
	}: {
		creatorId: string | null;
		assigneeId: string | null;
		participants: AttendeeAttrs[];
		hasAccessToDuedates: boolean;
		setBlockAttrs: (values: ProseNode["attrs"]) => unknown;
		reRenderEditor: () => void;
		afterInvite?: () => void;
		blockKey: string;
		getPos: () => number;
		noteId: string;
		eventGuid?: string;
		user?: AttendeeAttrs;
		streamName: string;
		streamType: string;
		streamHasExternalUsers: boolean;
		isTodoStream: boolean;
		editorView: EditorView;
		includeCopyTo: boolean;
		nodeType: string;
		completedBy: UserAttrs | null;
		done: boolean;
		statusCondition: StatusCondition;
	}) =>
	(_close: () => void): AssignableDropdownMenuSections => {
		const currentAssignee = participants.find(p => p.id === assigneeId || p.user?.id === assigneeId);
		const assigneeIds = assigneeId ? [assigneeId] : null;
		const currentAssignees = currentAssignee ? [currentAssignee] : [];
		const context = getContext(editorView.state);

		let actions: AssignableDropdownMenuSections = [
			{
				children: [
					getReassignMenuItem({
						view: editorView,
						reassignMenu: getReassignMenu({
							afterInvite,
							noModal: true,
							streamName,
							displayedAssigneeOptions: participants,
							allAssigneeOptions: participants,
							updateItemAttrs: setBlockAttrs,
							currentAssigneeMenuItem: getCurrentAssigneeMenuItem(
								{ currentAssignees, user, currentAssigneeIds: assigneeIds, completedBy, done, statusCondition },
								{ icon: faTimes },
							),
							currentAssignees,
							streamType,
							isTodoStream,
							creatorId,
							noteId,
							blockKey,
							eventGuid,
							reRenderEditor,
							nodeType,
						}),
						currentAssigneeMenuItem: getCurrentAssigneeMenuItem(
							{ currentAssignees, user, currentAssigneeIds: assigneeIds, completedBy, done, statusCondition },
							{ icon: faChevronRight },
						),
						hasAccessToOriginalNote: true,
						originalBlockIds: [],
						blockIdsToNoteIds: {},
						isTeams: false,
					}),
				],
			},
		];

		if (includeCopyTo && !context.isPrivateNote && editorView.editable) {
			actions.push({
				children: [
					{
						...getCopyToMenuItem(),
						action: (close: () => void) => {
							close();
							__globalModalContext?.open(({ close }) => (
								<CopyToModalRenderer ids={[{ noteId, key: blockKey }]} close={close} />
							));
							return true;
						},
						disabled: streamHasExternalUsers,
						streamHasExternalUsers: streamHasExternalUsers,
					},
				],
			});
		}

		actions.push(
			{
				children: context.isMeetingModal
					? [getReactionMenuItem({ blockKey, editorView })]
					: !context.isPrivateNote
						? [getReactionMenuItem({ blockKey, editorView }), getCommentMenuItem({ view: editorView, blockKey })]
						: [],
			},
			{
				children: context.isMeetingModal
					? [getDeleteBlockMenu({ view: editorView, getPos, text: "Delete" })]
					: [
							...(noteId ? [getCopyLinkMenuItem({ noteId: noteId, blockKey })] : []),
							getDeleteBlockMenu({ view: editorView, getPos, text: "Delete" }),
						],
			},
			{
				children: !context.isPrivateNote
					? [
							getCreatorMenuItem({
								creator: participants.find(u => u.id === creatorId) ?? null,
								user,
								isAiGenerated: false,
							}),
						]
					: [],
			},
		);

		return participants.length > 0 ? actions : [];
	};

export const getCustomSectionTips =
	() =>
	(_close: () => void): AssignableDropdownMenuSections => {
		const actions = [
			{
				children: [
					{
						id: "custom-section-tips",
						children: (
							<div className={styles.itemContent}>
								<div className={cx(styles.itemContentLeft, styles.customSectionTips__icon)}>
									<FontAwesomeIcon icon={faUser} />
								</div>
								<div className={styles.itemContentRight}>
									<div className={styles.customSectionTips__title}>Assigned to</div>
									<div className={styles.customSectionTips__subtitle}>Current User</div>
								</div>
							</div>
						),
						readOnly: true,
					},
				],
			},
			{
				children: [
					{
						id: "custom-section-tips-text",
						children: (
							<>
								<div className={styles.customSectionTips__text}>
									Item will be assigned by default to the user who adds it.{" "}
									<HelpCenterLink articleId="5992965">Learn more</HelpCenterLink>
								</div>
							</>
						),
						readOnly: true,
					},
				],
			},
		];
		return actions;
	};

export const AssignableDropdownMenu: React.FC<{
	menuSections: (close: () => void) => AssignableDropdownMenuSections;
	close: () => void;
	initialSubView?: string;
	setIsWide?: (val: boolean) => void;
	setContainerClass?: (val: string) => void;
}> = ({ menuSections, close, initialSubView, setIsWide, setContainerClass }) => {
	const [activeId, setActiveId] = React.useState<string | undefined>();
	const menuItems = React.useMemo(() => {
		let res = OrderedMap<string, AssignableDropdownItemType>();
		menuSections(close).forEach(sec => {
			sec?.children.forEach(item => {
				res = res.set(item.id, item);
			});
		});
		return res;
	}, [menuSections, close]);

	let initialActiveSubviewId = "";
	let initialStack: NonNullable<AssignableDropdownItemType["renderSubView"]>[] = [];

	if (initialSubView) {
		const item = menuItems.get(initialSubView);
		if (item && item.renderSubView) {
			initialActiveSubviewId = item.id;
			initialStack = [item.renderSubView];
		}
	}

	const [stack, setStack] = React.useState<NonNullable<AssignableDropdownItemType["renderSubView"]>[]>(initialStack);
	const [activeSubviewId, setActiveSubviewId] = React.useState(initialActiveSubviewId);

	const refs: React.RefObject<{ [key: string]: React.RefObject<HTMLDivElement> }> = React.useRef({});
	const pushView = React.useCallback(
		(render: NonNullable<AssignableDropdownItemType["renderSubView"]>) => {
			setStack(s => {
				return [...s, render];
			});
			return false;
		},
		[setStack],
	);
	const popView = React.useCallback(() => {
		setStack(s => {
			return [...s.slice(0, s.length - 1)];
		});
	}, [setStack]);
	const selectNext = React.useCallback(
		(direction: 1 | -1 = 1) => {
			const menuIds = menuItems.keySeq().toArray();
			let nextIndex = menuIds.indexOf(activeId || "");
			do {
				nextIndex = nextIndex + direction;
				if (nextIndex < 0) {
					nextIndex = menuIds.length + nextIndex;
				}
				nextIndex %= menuIds.length;
			} while (menuItems.get(menuIds[nextIndex])?.readOnly);
			const nextItemId = menuIds[nextIndex];
			setActiveId(nextItemId);
			const nextItem = menuItems.get(nextItemId);
			if (nextItem.onActive) {
				nextItem.onActive();
			}
			if (nextItemId && refs.current) {
				refs.current[nextItemId].current?.scrollIntoView({ block: "nearest" });
			}
		},
		[activeId, setActiveId, menuItems, refs],
	);

	const kbListener = React.useCallback(
		(e: KeyboardEvent) => {
			function closeAndClean() {
				document.removeEventListener("keydown", kbListener, true);
				close();
			}
			let handled = true;
			switch (e.key) {
				case "Escape":
					close();
					break;
				case "Tab":
					selectNext(e.shiftKey ? -1 : 1);
					break;
				case "ArrowDown":
					selectNext(1);
					break;
				case "ArrowUp":
					selectNext(-1);
					break;
				case "Enter": {
					if (!activeId) return;
					const item = menuItems.get(activeId);
					if (item) {
						if (item.renderSubView) {
							pushView(item.renderSubView);
						} else if (item.action) {
							item.action(closeAndClean);
						}
						setActiveSubviewId(item.id);
					}
					break;
				}
				default:
					handled = false;
					break;
			}
			if (handled) {
				e.preventDefault();
				e.stopPropagation();
			}
		},
		[activeId, selectNext, pushView, menuItems, close],
	);

	const closeAndCleanListeners = React.useCallback(() => {
		document.removeEventListener("keydown", kbListener, true);
		return close();
	}, [kbListener, close]);

	React.useEffect(() => {
		if (stack.length > 0) return noop;
		document.addEventListener("keydown", kbListener, true);

		return () => document.removeEventListener("keydown", kbListener, true);
	}, [stack.length, kbListener]);

	const setActive = React.useCallback(
		(id: string) => () => {
			setActiveId(id);
		},
		[setActiveId],
	);

	// Effect to handle the change width, depending on subview
	React.useLayoutEffect(() => {
		if (!setIsWide) {
			return;
		}

		setIsWide(activeSubviewId === "add-reaction");
	}, [activeSubviewId, setIsWide]);
	React.useLayoutEffect(() => {
		const item = menuItems.get(activeSubviewId);
		if (item?.containerClassName) {
			setContainerClass?.(item.containerClassName);
		} else {
			setContainerClass?.("");
		}
	}, [setContainerClass, activeSubviewId, menuItems]);

	const topView = stack.length > 0 ? stack[stack.length - 1] : undefined;
	const stackContents = React.useMemo(() => topView?.(close, popView), [topView, close, popView]);

	if (stack.length > 0) {
		return <>{stackContents}</>;
	}
	const sections = menuSections(close);
	const itemRefs = refs.current || {};
	return (
		<>
			{sections.map((sec, i) => {
				// Sections could be empty, example: just one participant with a TP assigned
				if (!sec || sec.children.length === 0) return undefined;
				return (
					// eslint-disable-next-line react/no-array-index-key
					<div className={cx(styles.menuItemsSection, sec.sectionProps?.className || "")} key={i}>
						{sec.children
							.filter(Boolean)
							.map(
								({
									id,
									renderSubView,
									action: originalAction,
									containerClassName: _c,
									streamHasExternalUsers,
									...props
								}) => {
									if (!itemRefs[id]) {
										itemRefs[id] = React.createRef();
									}
									let action = originalAction;
									if (renderSubView) {
										action = () => {
											setActiveSubviewId(id);
											return pushView(renderSubView);
										};
									}
									const assignableDropdownItemComponent = (
										<AssignableDropdownItem
											{...props}
											ref={itemRefs[id]}
											close={closeAndCleanListeners}
											action={action}
											key={id}
											active={activeId === id}
											setActive={setActive(id)}
										/>
									);

									if (streamHasExternalUsers) {
										return (
											<DisabledOnStreamWithExternalUsersTooltip
												showTooltip={true}
												tooltipContent={"You cannot do this on note with external users."}
											>
												{assignableDropdownItemComponent}
											</DisabledOnStreamWithExternalUsersTooltip>
										);
									} else {
										return assignableDropdownItemComponent;
									}
								},
							)}
					</div>
				);
			})}
		</>
	);
};

enum DropdownDirection {
	UP = "up",
	DOWN = "down",
}
type AssignableDropdownProps = {
	popperStyle?: React.CSSProperties;
	anchor?: HTMLElement;
	portal: HTMLElement | null;
	menuSections: (close: () => void) => AssignableDropdownMenuSections;
	onClose: () => void;
	className?: string;
	initialSubView?: string;
	editorView?: EditorView;
	relativeToPmWrapper?: boolean;
	doNotOverflowEditor?: boolean;
	forceDesktop?: boolean;
};
const AssignableDropdown = React.forwardRef<HTMLDivElement, AssignableDropdownProps>(function AssignableDropdown(
	{
		popperStyle,
		anchor,
		portal,
		menuSections,
		onClose,
		className = "",
		initialSubView,
		editorView,
		relativeToPmWrapper,
		doNotOverflowEditor,
		forceDesktop,
	},
	popperRef,
) {
	const [direction, setDirection] = React.useState<DropdownDirection | null>(null);
	const [isWide, setIsWide] = React.useState(false);
	const [sizeFlag, setSizeFlag] = React.useState<number>(new Date().getTime());
	const [containerClass, setContainerClass] = React.useState("");

	const editorViewRect = React.useMemo<{
		height: number;
		width: number;
		x: number;
		y: number;
		top: number;
		bottom: number;
	}>(() => {
		if (!editorView?.dom.parentElement) return { height: 0, width: 0, x: 0, y: 0, top: 0, bottom: 0 };
		return editorView.dom.parentElement?.getBoundingClientRect();
	}, [editorView, sizeFlag]); // eslint-disable-line react-hooks/exhaustive-deps

	const anchorRect = React.useMemo<{
		height: number;
		width: number;
		x: number;
		y: number;
		top: number;
		bottom: number;
	}>(() => {
		if (!anchor) return { height: 0, width: 0, x: 0, y: 0, top: 0, bottom: 0 };
		return anchor.getBoundingClientRect();
	}, [anchor, sizeFlag]); // eslint-disable-line react-hooks/exhaustive-deps

	const ref = useForwardedRef<HTMLDivElement>(popperRef);
	const { isMobile } = React.useContext(ViewportContext);
	const { inPanel, view, renderingContext } = React.useContext(ActionItemsContext);
	const inRecording = view === Views.RECORDING;
	const inSeriesPanel = renderingContext === "seriesPanel";

	const close = React.useCallback(() => {
		const element = ref.current;
		if (element) {
			element.classList.add(styles.dropdownClosing);
			const closeCb = () => {
				element.classList.remove(styles.dropdownClosing);
				onClose();
			};
			element.addEventListener("transitionend", closeCb, { once: true });
			element.addEventListener("animationend", closeCb, { once: true });
		} else {
			// Some times, the element can be already removed when we close the menu.
			// Make sure the onClose is called as well
			onClose();
		}
	}, [onClose, ref]);

	const getRefBox = React.useCallback(() => {
		if (!ref.current) {
			setDirection(null);
			return;
		}
		return ref.current.getBoundingClientRect();
	}, [ref]);

	React.useEffect(() => {
		if (isMobile && !forceDesktop) return;
		const refBox = getRefBox();
		if (!refBox || !refBox.height) return;
		if (relativeToPmWrapper && !!editorView) {
			// when dropdown is relative to pmWrapper, instead of comparing to window, to the lower boundary of the pmWrapper
			if (refBox.height + anchorRect.y + anchorRect.height > editorViewRect.y + editorViewRect.height) {
				setDirection(DropdownDirection.UP);
			} else {
				setDirection(DropdownDirection.DOWN);
			}
		} else if (refBox.height + anchorRect.y > window.innerHeight) {
			setDirection(DropdownDirection.UP);
		} else {
			setDirection(DropdownDirection.DOWN);
		}
	}, [
		setDirection,
		ref,
		anchorRect,
		isMobile,
		relativeToPmWrapper,
		editorView,
		editorViewRect,
		getRefBox,
		forceDesktop,
	]);

	/* The parent of the kebab menu portal is the kebab menu icon ---
			Positioning needs to be related to the DOM rectangle of this icon */
	const positionStyle = React.useMemo(() => {
		if (!ref || !direction) {
			return {
				position: "fixed",
				top: -9999,
			} as const;
		}

		const refBox = getRefBox();
		if (!refBox || !refBox.height) return;

		if (relativeToPmWrapper && !!editorView) {
			const fromViewTopToAnchorTop = anchorRect.y - editorViewRect.y;
			const fromViewRightToAnchorRight = editorViewRect.x + editorViewRect.width - anchorRect.x - anchorRect.width;
			return {
				[DropdownDirection.UP]: {
					top: fromViewTopToAnchorTop - refBox.height - 10,
					right: fromViewRightToAnchorRight,
				},
				[DropdownDirection.DOWN]: {
					top: fromViewTopToAnchorTop + anchorRect.height + 10,
					right: fromViewRightToAnchorRight,
				},
			}[direction];
		}
		let top = -refBox.height / 2;
		if (!anchorRect || !editorViewRect || !doNotOverflowEditor) return { top };
		// Making sure that the dropdown doesn't overflow the editor view rect
		const spaceLeftAtTheTop = anchorRect.top - editorViewRect.top;
		const spaceLeftAtTheBottom = editorViewRect.bottom - anchorRect.bottom;
		// If it overflows in the top
		if (-top > spaceLeftAtTheTop) {
			// We position the dropdown downwards
			top = 0;
		} else if (top + refBox.height > spaceLeftAtTheBottom) {
			// We position the dropdown upwards
			top = -refBox.height;
		}
		return { top };
	}, [ref, direction, getRefBox, relativeToPmWrapper, editorView, anchorRect, editorViewRect, doNotOverflowEditor]);

	React.useEffect(() => {
		const onResize = debounce(() => {
			setSizeFlag(new Date().getTime());
		}, 100);
		window.addEventListener("resize", onResize);
		return () => {
			window.removeEventListener("resize", onResize);
		};
	}, []);

	useBlur(ref, close, true);
	React.useEffect(() => {
		return () => {
			onClose();
		};
	}, [onClose]);

	const dontUsePopper = isZoom;

	const menuStyle = React.useMemo(
		() => ((inPanel || inRecording || inSeriesPanel) && !dontUsePopper ? popperStyle : { ...positionStyle }),
		[dontUsePopper, inPanel, inRecording, inSeriesPanel, popperStyle, positionStyle],
	);

	if (!portal) return null;

	return ReactDOM.createPortal(
		<>
			<div className={cx(styles.dropdownBackdrop)} />
			<div
				className={cx(
					styles.dropdown,
					{
						[styles.dropdownVisible]: !!anchor,
						[styles.wide]: isWide,
					},
					className,
					containerClass,
				)}
				style={menuStyle}
				ref={ref}
				tabIndex={0}
				contentEditable={false}
			>
				{!!anchor && (
					<>
						<AssignableDropdownMenu
							menuSections={menuSections}
							close={close}
							initialSubView={initialSubView}
							setIsWide={setIsWide}
							setContainerClass={setContainerClass}
						/>
					</>
				)}
			</div>
		</>,
		portal,
	);
});

export default AssignableDropdown;
